import { useCallback } from 'react';
import { Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { appAlert } from '../utils/appAlert';

interface UseImagePickerOptions {
  aspect?: [number, number];
  quality?: number;
  allowsEditing?: boolean;
}

const defaults: Required<UseImagePickerOptions> = {
  aspect: [1, 1],
  quality: 0.8,
  allowsEditing: true,
};

export function useImagePicker(options?: UseImagePickerOptions) {
  const config = { ...defaults, ...options };

  const pickFromLibrary = useCallback(async (): Promise<string | null> => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      appAlert('Permission Required', 'Please allow access to your photo library.');
      return null;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: config.allowsEditing,
      aspect: config.aspect,
      quality: config.quality,
    });

    if (!result.canceled && result.assets[0]) {
      return result.assets[0].uri;
    }
    return null;
  }, [config.allowsEditing, config.aspect, config.quality]);

  const takePhoto = useCallback(async (): Promise<string | null> => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      appAlert('Permission Required', 'Please allow access to your camera.');
      return null;
    }

    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: config.allowsEditing,
      aspect: config.aspect,
      quality: config.quality,
    });

    if (!result.canceled && result.assets[0]) {
      return result.assets[0].uri;
    }
    return null;
  }, [config.allowsEditing, config.aspect, config.quality]);

  const showPicker = useCallback(
    async (onImageSelected: (uri: string) => void) => {
      if (Platform.OS === 'web') {
        const uri = await pickFromLibrary();
        if (uri) onImageSelected(uri);
      } else {
        appAlert('Add Photo', 'Choose an option', [
          {
            text: 'Camera',
            onPress: async () => {
              const uri = await takePhoto();
              if (uri) onImageSelected(uri);
            },
          },
          {
            text: 'Photo Library',
            onPress: async () => {
              const uri = await pickFromLibrary();
              if (uri) onImageSelected(uri);
            },
          },
          { text: 'Cancel', style: 'cancel' },
        ]);
      }
    },
    [pickFromLibrary, takePhoto]
  );

  return { showPicker, pickFromLibrary, takePhoto };
}
